import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { Tabs } from "@freightsimple/ui-core/PageLayout/Tabs";

import { PlusIcon } from "@phosphor-icons/react/Plus";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import PageTitle from "@/Shared/Layout/PageTitle";
import type { UUID } from "@/Shared/types";
import { ContactsManageDialog } from "@/Shared/Contacts/Dialogs/ContactsManageDialog";
import { ManageContactsDialogFlavour } from "@/Shared/Contacts/ManageContactsDialogFlavour";
import { useFilterSavedLocations } from "@/Shared/Locations/Helpers/locationsFilterSavedLocation";
import type { FilterShipmentsDescription } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";
import { shipmentFilterRenderDescriptionAsQueryParams } from "@/Shared/ShipmentFilter/Helpers/shipmentFilterRenderDescriptionAsQueryParams";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useOnce } from "@/Hooks/useOnce";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { useLocationsAddLocationDialog } from "@/Shared/Locations/Hooks/useLocationsAddLocationDialog";
import { useLocationsModifyLocationDialog } from "@/Shared/Locations/Hooks/useLocationsModifyLocationDialog";
import { LocationsSavingBehaviourType } from "@/Shared/Locations/Types/LocationsSavingBehaviourType";
import { useSavedLocationsService } from "@/Shared/Locations/Services/LocationsSavedService";
import { SortShipmentOptions } from "@/Shared/Shipment/SortShipmentOptions";
import { useLocationsOperations } from "./Hooks/useLocationsOperations";
import { LocationsAddLocationButton } from "./Internal/LocationsAddLocationButton";
import { LocationsAddressBookTabContent } from "./Internal/LocationsAddressBookTabContent";
import { LocationsAddressBookTabs } from "./Internal/LocationsAddressBookTabs";

const AddressBookScreen: React.FC = () => {
  const savedLocationsService = useSavedLocationsService();

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const doFilter = useFilterSavedLocations(filter);
  const navigate = useNavigate();

  const [locations, setLocations] = useState<
    undefined | Array<SavedLocation>
  >();

  const [selectedTab, setSelectedTab] = useState<LocationsAddressBookTabs>(
    LocationsAddressBookTabs.FavouriteLocations,
  );
  const [defaultPickupLocationId, setDefaultPickupLocationId] = useState<
    string | undefined
  >();
  const [defaultDeliveryLocationId, setDefaultDeliveryLocationId] = useState<
    string | undefined
  >();
  const savedLocationsApi = useSavedLocationsApi();

  async function loadSavedLocations() {
    setLoading(true);
    const savedLocationsResponse =
      await savedLocationsApi.getAllSavedLocations();
    setLocations(
      savedLocationsResponse.items.sort((a, b) => {
        return a.location.businessName.localeCompare(b.location.businessName);
      }),
    );
    setDefaultPickupLocationId(savedLocationsResponse.defaultPickupLocationId);
    setDefaultDeliveryLocationId(
      savedLocationsResponse.defaultDeliveryLocationId,
    );
    setLoading(false);

    if (savedLocationsResponse.items.some((i) => i.isFavourite)) {
      setSelectedTab(LocationsAddressBookTabs.FavouriteLocations);
    } else if (savedLocationsResponse.items.some((i) => i.isBranch)) {
      setSelectedTab(LocationsAddressBookTabs.BranchLocations);
    } else {
      setSelectedTab(LocationsAddressBookTabs.AllLocations);
    }
  }

  useOnce(loadSavedLocations);

  async function onNewLocation(location: SavedLocation, addAsBranch: boolean) {
    location.isBranch = addAsBranch;
    await savedLocationsService.addLocation(location);
    await loadSavedLocations();
  }

  const {
    loadingLocations,
    onModifyLocation,
    onDelete,
    onToggleFavourite,
    onToggleBranch,
    onToggleDefaultPickupLocation,
    onToggleDefaultDeliveryLocation,
    dialogs: locationOperationsDialogs,
  } = useLocationsOperations();

  const { show: showAddLocationDialog, dialogs: addLocationDialogs } =
    useLocationsAddLocationDialog(
      LocationContext.Any,
      LocationsSavingBehaviourType.Always,
      async function (data) {
        await onNewLocation(
          data.location,
          selectedTab === LocationsAddressBookTabs.BranchLocations,
        );
      },
    );

  const { show: showModifyLocationDialog, dialogs: modifyLocationDialogs } =
    useLocationsModifyLocationDialog(LocationContext.Any, function (data) {
      onModifyLocation(
        data.savedLocationId,
        data.location,
        setLocations,
        data.locationHasDock,
        data.locationHasForklift,
      );
    });

  const [manageContactsLocationId, setManageContactsLocationId] = useState<
    UUID | undefined
  >();

  function filterSavedLocations() {
    return locations
      ?.filter(function (location) {
        if (selectedTab === LocationsAddressBookTabs.FavouriteLocations) {
          if (!location.isFavourite) {
            return false;
          } else {
            return true;
          }
        } else if (selectedTab === LocationsAddressBookTabs.BranchLocations) {
          if (!location.isBranch) {
            return false;
          } else {
            return true;
          }
        } else {
          return true;
        }
      })
      .filter(doFilter);
  }

  const filteredLocations = filterSavedLocations();

  useHotkeys("ctrl+l", () => showAddLocationDialog());

  async function onViewShipmentsToThisLocation(savedLocation: SavedLocation) {
    const params: FilterShipmentsDescription = {
      deliveryBusinessName: savedLocation.location.businessName,
      deliveryPostalCode: savedLocation.location.address.postalCode,
    };
    navigate(
      `/view-all-shipments?${shipmentFilterRenderDescriptionAsQueryParams(
        params,
        SortShipmentOptions.PickupDate,
      )}`,
    );
  }

  async function onViewShipmentsFromThisLocation(savedLocation: SavedLocation) {
    const params: FilterShipmentsDescription = {
      pickupBusinessName: savedLocation.location.businessName,
      pickupPostalCode: savedLocation.location.address.postalCode,
    };
    navigate(
      `/view-all-shipments?${shipmentFilterRenderDescriptionAsQueryParams(
        params,
        SortShipmentOptions.PickupDate,
      )}`,
    );
  }

  const numberFavouriteLocations = locations
    ?.filter(doFilter)
    .filter((sl) => sl.isFavourite).length;
  const numberBranchLocations = locations
    ?.filter(doFilter)
    .filter((sl) => sl.isBranch).length;
  const numberAllLocations = locations?.filter(doFilter).length;

  // One body for all three tabs: it already switches on `selectedTab`, and
  // Tabs only mounts the open tab's children.
  const tabBody = (
    <LocationsAddressBookTabContent
      filter={filter}
      showAddLocationDialog={showAddLocationDialog}
      filteredLocations={filteredLocations || []}
      showModifyLocationDialog={showModifyLocationDialog}
      showChangeContactDialog={async function (savedLocationId: UUID) {
        setManageContactsLocationId(savedLocationId);
      }}
      onToggleFavourite={(savedLocationId) =>
        onToggleFavourite(savedLocationId, locations, setLocations)
      }
      onToggleBranch={(savedLocationId) =>
        onToggleBranch(savedLocationId, locations, setLocations)
      }
      onDelete={(savedLocationId, savedLocation) =>
        onDelete(
          savedLocationId,
          savedLocation,
          setLocations,
          setDefaultPickupLocationId,
          setDefaultDeliveryLocationId,
        )
      }
      onToggleDefaultPickupLocation={(savedLocationId) =>
        onToggleDefaultPickupLocation(
          savedLocationId,
          defaultPickupLocationId,
          setDefaultPickupLocationId,
        )
      }
      onToggleDefaultDeliveryLocation={(savedLocationId) =>
        onToggleDefaultDeliveryLocation(
          savedLocationId,
          defaultDeliveryLocationId,
          setDefaultDeliveryLocationId,
        )
      }
      defaultPickupLocationId={defaultPickupLocationId}
      defaultDeliveryLocationId={defaultDeliveryLocationId}
      selectedTab={selectedTab}
      onViewShipmentsToThisLocation={onViewShipmentsToThisLocation}
      onViewShipmentsFromThisLocation={onViewShipmentsFromThisLocation}
      loadingLocations={loadingLocations}
    />
  );

  return (
    <>
      <PageTitle>Address Book</PageTitle>
      {/* Dialogs live outside the layout so they work from the empty state */}
      {addLocationDialogs}
      {modifyLocationDialogs}
      {locationOperationsDialogs}
      <ContactsManageDialog
        open={manageContactsLocationId !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setManageContactsLocationId(undefined);
            // The legacy flow reloaded locations whenever the modal closed.
            loadSavedLocations();
          }
        }}
        savedLocationId={manageContactsLocationId}
        flavour={ManageContactsDialogFlavour.AddressBook}
        onDone={() => {
          setManageContactsLocationId(undefined);
          loadSavedLocations();
        }}
      />
      <FullWidthLayout
        header="Address Book"
        rightContent={
          <Button primary icon={PlusIcon} onClick={showAddLocationDialog}>
            Add New Location
          </Button>
        }
        microcopy="Your address book contains every location you've shipped from or to. You can add favourite locations to use in future shipments."
        loading={loading}
        showEmptyState={locations?.length === 0}
        emptyState={
          <Stack align="center" gap={8}>
            <Stack align="center" gap={1}>
              <Heading level={1}>Your address book is empty</Heading>
              <T size="base" color="secondary">
                Your address book contains every location you've shipped from or
                to. You can favourite locations, mark them as default for pickup
                or delivery
              </T>
            </Stack>
            <LocationsAddLocationButton onClick={showAddLocationDialog} />
          </Stack>
        }
        content={
          <>
            {/* Controlled: the screen owns the open tab because the header
                  "Add New Location" button and the ctrl+L shortcut both sit
                  outside the tabs and still need to know whether to mark the new
                  location as a branch. The filter goes in Tabs' `controls` slot,
                  which is what the old hand-rolled bar existed to provide. */}
            <Tabs
              activeTab={selectedTab}
              onTabChange={(tabId) =>
                setSelectedTab(tabId as LocationsAddressBookTabs)
              }
              controls={
                locations?.length === undefined || locations?.length > 0 ? (
                  // Plain wrapper: the address-book smoke test looks the input
                  // up as a descendant of this testid, so it can't move onto
                  // the input itself.
                  <div data-testid="filter-locations-input">
                    <SearchInput
                      value={filter}
                      onChange={setFilter}
                      placeholder="Filter locations..."
                    />
                  </div>
                ) : undefined
              }
            >
              <Tab
                id={LocationsAddressBookTabs.FavouriteLocations}
                testId="tab-favourite-locations"
                title="Favourite Locations"
                count={numberFavouriteLocations}
              >
                {tabBody}
              </Tab>
              <Tab
                id={LocationsAddressBookTabs.BranchLocations}
                testId="tab-branch-locations"
                title="Branch Locations"
                count={numberBranchLocations}
              >
                {tabBody}
              </Tab>
              <Tab
                id={LocationsAddressBookTabs.AllLocations}
                testId="tab-all-locations"
                title="All Locations"
                count={numberAllLocations}
              >
                {tabBody}
              </Tab>
            </Tabs>
          </>
        }
      />
    </>
  );
};

export default AddressBookScreen;
